import { useEffect, useState } from 'react'

/**
 * Viewport breakpoint hook.
 *
 * Breakpoints (locked in alongside the shell-polish slice):
 *   - mobile  : width < 720
 *   - tablet  : 720 <= width < 1100
 *   - desktop : width >= 1100
 *
 * SSR-safe: defaults to a desktop viewport on first render so server output
 * matches what the desktop client paints (the most common case). The hook
 * snaps to the real value on first effect.
 *
 * Resize is debounced ~50ms to avoid thrashing during continuous drags.
 */

export const VIEWPORT_BREAKPOINT_MOBILE = 720
export const VIEWPORT_BREAKPOINT_DESKTOP = 1100

export interface Viewport {
  width: number
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
}

const SSR_DEFAULT_WIDTH = 1280
const RESIZE_DEBOUNCE_MS = 50

function classify(width: number): Viewport {
  const isMobile = width < VIEWPORT_BREAKPOINT_MOBILE
  const isDesktop = width >= VIEWPORT_BREAKPOINT_DESKTOP
  const isTablet = !isMobile && !isDesktop
  return { width, isMobile, isTablet, isDesktop }
}

function readWidth(): number {
  if (typeof window === 'undefined') return SSR_DEFAULT_WIDTH
  return window.innerWidth
}

export function useViewport(): Viewport {
  const [viewport, setViewport] = useState<Viewport>(() => classify(readWidth()))

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    // Snap to the real viewport on mount in case SSR default was wrong.
    setViewport(classify(window.innerWidth))

    let timer: ReturnType<typeof setTimeout> | null = null

    function onResize(): void {
      if (timer !== null) clearTimeout(timer)
      timer = setTimeout(() => {
        setViewport(classify(window.innerWidth))
      }, RESIZE_DEBOUNCE_MS)
    }

    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      if (timer !== null) clearTimeout(timer)
    }
  }, [])

  return viewport
}
